import { getCordataWemTriples } from "@biblioteksentralen/bmdb-search-client";
import {
  formatIsbdTitle,
  FormatIsbdTitleOptions,
  isInteractiveWork,
  isMovingImageWork,
  Manifestation,
  Work,
} from "@biblioteksentralen/cordata";
import { getPath, normalizeIsbn } from "@libry-content/common";
import { sift, sort, unique } from "radash";
import { getFormatOrderIndex } from "./documentTypes";
import {
  createLanguageCodeOrder,
  formatLanguagesList,
  getLanguagesSignature,
  getSortedLanguagesListIndex,
  handleLanguages,
} from "./languages";
import { CordataWorkTriple, getContributorsWithPath, sortWorkTriples } from "./CordataWorkTriple";
import { LibrarySystem } from "@libry-content/types";
import { supportedExternalContentServices } from "./externalContentService";
import { sortByMultiple } from "@biblioteksentralen/utils";
import { Holdings } from "../holdings";
import { VariantSignatures } from "./CordataVariant";
import { AtLeastOne, isAtLeastOne } from "@biblioteksentralen/types";

export type WorkTripleIdentifier = { manifestationId?: string; isbn?: string };

type Filters = {
  librarySystem?: LibrarySystem;
  manifestationIds?: string[];
};

export type PartedCordataWorkTriple = { workTriple: CordataWorkTriple; number: string };

export class CordataWork {
  private work: Work;
  private representativeManifestationId?: string;

  constructor(work: Work, representativeManifestationId?: string) {
    this.work = work;
    this.representativeManifestationId = representativeManifestationId;
  }

  // TODO: We dont want to provide the original work object directly, so at a later stage we should be using CordataWork / CordataWorkTriple instead where work is needed
  /**@deprecated */
  public getRawBmdbWork() {
    return this.work;
  }

  public get isMovie() {
    return isMovingImageWork(this.work);
  }

  public get isInteractive() {
    return isInteractiveWork(this.work);
  }

  public get id() {
    return this.work.id;
  }

  public getWorkTriple(workTripleIdentifier: WorkTripleIdentifier) {
    const triple = getCordataWemTriples(this.work).find(({ manifestation }) => {
      if ("manifestationId" in workTripleIdentifier) return manifestation.id === workTripleIdentifier.manifestationId;

      return manifestation.isbn.some((isbn) => normalizeIsbn(isbn) === normalizeIsbn(workTripleIdentifier.isbn));
    });

    if (!triple) return undefined;
    return new CordataWorkTriple(triple);
  }

  public get representativeWorkTriple() {
    return this.getWorkTriple({ manifestationId: this.representativeManifestationId });
  }

  public getWorkTriples(filters?: Filters) {
    const workTriples = getCordataWemTriples(this.work).map((triple) => new CordataWorkTriple(triple));
    if (filters)
      return workTriples.filter((triple) => {
        //TODO: filter out e-medium triples as we do not support it yet. E-medium appears in global search, and therefore appears in bmdb-sync jobs. Remove when appropriate
        const isNotEMedium = triple.documentType?.format !== "E-lydbok" && triple.documentType?.format !== "E-bok";
        // check if triple is in catalogue if library system is provided
        const isInCatalogue = !filters?.librarySystem || triple.isInCatalogue(filters.librarySystem);
        const manifestationIdIsIncluded =
          !filters?.manifestationIds || filters.manifestationIds?.includes(triple.manifestationId);
        return isInCatalogue && manifestationIdIsIncluded && isNotEMedium;
      });
    return sortWorkTriples(workTriples);
  }

  public get mainContributor() {
    return getContributorsWithPath(sift([this.contributors.find((contributor) => contributor.isMainContributor)]))[0];
  }

  public getWorkCoverImage(filters?: Filters) {
    if (this.representativeWorkTriple?.coverImageUrl) return this.representativeWorkTriple.coverImageUrl;
    return this.getWorkTriples(filters).find((triple) => triple.coverImageUrl)?.coverImageUrl;
  }

  public get contributors() {
    return getContributorsWithPath(this.work.contributors);
  }

  public get workYear() {
    return this.work.workYear;
  }

  public get title() {
    return this.work.title;
  }

  public get languages() {
    return this.work.languages;
  }

  public get languagesSignature() {
    return getLanguagesSignature(this.languages);
  }

  public get path() {
    return getPath(this.work);
  }

  public get hasPartTitles() {
    return this.title.titleType !== "uniform_title_music" && (!!this.title.partTitle || !!this.title.partNumber);
  }

  public get formattedTitle() {
    return formatIsbdTitle(this.work);
  }

  //get title for presenting work, along with language tag
  public getRepresentativeTitleData(options?: FormatIsbdTitleOptions) {
    if (this.representativeWorkTriple)
      return {
        formattedTitle: this.representativeWorkTriple.getFormattedTitle(options),
        titleLanguagesSignature: this.representativeWorkTriple.languagesSignature,
      };

    return { formattedTitle: this.formattedTitle, titleLanguagesSignature: this.languagesSignature };
  }

  public getGenres(form?: "form" | "nonForm") {
    return unique(this.work.genres.filter((genre) => !form || genre.isForm === (form === "form")));
  }

  public get ageGroups() {
    return this.work.ageGroups;
  }

  public get subjects() {
    return this.work.subjects;
  }

  public get summary() {
    return this.work.summary;
  }

  public get workTypes() {
    return this.work.workTypes;
  }

  public get ageLimit() {
    return this.work.ageLimit;
  }

  public get type() {
    return this.work.type;
  }

  public getLanguages(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    const languages = sift(
      unique(
        workTriples.map(({ languages }) => languages).filter((languages) => !!languages),
        (languages) => getLanguagesSignature(languages)
      )
    );
    const languageCodeOrder = createLanguageCodeOrder();
    return sort(languages.map(handleLanguages(languageCodeOrder)), (languagesList) =>
      getSortedLanguagesListIndex(languagesList, languageCodeOrder)
    ).filter((languages) => languages.length);
  }

  public getFormattedLanguages({
    filters,
    capitalizeFirstLetters = "none",
  }: {
    filters?: Filters;
    capitalizeFirstLetters?: "all" | "first" | "none";
  }) {
    return this.getLanguages(filters).map((languageList, index) => {
      const capitalizeFirstLetter =
        capitalizeFirstLetters === "none" ? false : capitalizeFirstLetters === "all" ? true : index === 0;
      return formatLanguagesList(languageList, { capitalizeFirstLetter });
    });
  }

  public getDocumentTypes(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    const uniqueDocumentTypes = sift(
      unique(
        workTriples.filter(({ documentTypeLabel }) => !!documentTypeLabel),
        ({ documentTypeLabel }) => documentTypeLabel!
      ).map(({ documentType }) => documentType)
    );
    return sort(uniqueDocumentTypes, ({ format }) => getFormatOrderIndex(format));
  }

  public getMediaTypeLabels(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    return unique(sift(workTriples.map((triple) => triple.mediaTypeLabel)));
  }

  public getDocumentTypesWithLanguagesSignature = (languagesSignature: string | undefined) => {
    const workTriplesWithLanguagesSignature = this.getWorkTriples().filter(
      (workTriple) => languagesSignature === workTriple.languagesSignature
    );
    const manifestationIds = workTriplesWithLanguagesSignature.map(({ manifestationId }) => manifestationId);
    return sort(this.getDocumentTypes({ manifestationIds }), ({ format }) => getFormatOrderIndex(format));
  };

  public getLanguagesWithDocumentTypeLabel = (documentTypeLabel: string | undefined) => {
    const workTriplesWithDocumentTypeLabel = this.getWorkTriples().filter(
      (workTriple) => documentTypeLabel === workTriple.documentTypeLabel
    );
    const manifestationIds = workTriplesWithDocumentTypeLabel.map(({ manifestationId }) => manifestationId);
    return this.getLanguages({ manifestationIds });
  };

  public getVariantsWithTriples(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    const variants = unique(
      workTriples.map(({ cordataVariant }) => cordataVariant),
      ({ signatures }) => signatures.totalSignature
    );
    const variantsWithWorkTriples = variants.map((uniqueVariant) => ({
      variant: uniqueVariant,
      workTriples: sortWorkTriples(
        workTriples.filter(
          ({ cordataVariant }) => cordataVariant.signatures.totalSignature === uniqueVariant.signatures.totalSignature
        )
      ),
    }));
    return sortByMultiple(
      variantsWithWorkTriples,
      ({ variant }) => getFormatOrderIndex(variant.documentType?.format),
      ({ variant }) => getSortedLanguagesListIndex(variant.languages ?? []),
      ({ variant }) =>
        supportedExternalContentServices.findIndex(
          ({ originName }) => originName === variant.externalContentService?.originName
        )
    );
  }

  public getSpecifiedVariantWithTriples(signatures: Partial<VariantSignatures>, filters?: Filters) {
    return this.getVariantsWithTriples(filters).find(({ variant }) => {
      if (signatures.totalSignature && signatures.totalSignature === variant.signatures.totalSignature) return true;
      const languagesMatch =
        !signatures.languagesSignature || signatures.languagesSignature === variant.signatures.languagesSignature;
      const documentTypeMatch =
        !signatures.documentTypeSignature ||
        signatures.documentTypeSignature === variant.signatures.documentTypeSignature;
      const externalContentServiceMatch =
        !signatures.externalContentServiceOriginSignature ||
        signatures.externalContentServiceOriginSignature === variant.signatures.externalContentServiceOriginSignature;
      return languagesMatch && documentTypeMatch && externalContentServiceMatch;
    });
  }

  public getTriplesPartedByTitle(filters?: Filters): AtLeastOne<PartedCordataWorkTriple> | undefined {
    const workTriples = this.getWorkTriples(filters);
    const triplesPartedByTitle = workTriples
      .map((workTriple) => ({ workTriple, number: workTriple.title.partNumber }))
      .filter((workTriple): workTriple is PartedCordataWorkTriple => !!workTriple.number);
    if (isAtLeastOne(triplesPartedByTitle)) return triplesPartedByTitle;
  }

  public getTriplesPartedBySeries(filters?: Filters): AtLeastOne<PartedCordataWorkTriple> | undefined {
    const workTriples = this.getWorkTriples(filters);
    const triplesPartedBySeries = workTriples
      .map((workTriple) => ({
        workTriple,
        number: workTriple.collections.find((collection) => collection.numberInSeries)?.numberInSeries,
      }))
      .filter((workTriple): workTriple is PartedCordataWorkTriple => !!workTriple.number);
    return isAtLeastOne(triplesPartedBySeries) ? triplesPartedBySeries : undefined;
  }

  public getPartedTriples(filters?: Filters) {
    return [...(this.getTriplesPartedByTitle(filters) ?? []), ...(this.getTriplesPartedBySeries(filters) ?? [])];
  }

  public getNonPartedTriples(filters?: Filters) {
    const partedTriples = this.getPartedTriples(filters);
    return this.getWorkTriples(filters).filter(
      (workTriple) =>
        !partedTriples.find((partedTriple) => partedTriple.workTriple.manifestationId === workTriple.manifestationId)
    );
  }

  public hasPartedTriples(filters?: Filters) {
    return !!this.getTriplesPartedByTitle(filters) || !!this.getTriplesPartedBySeries(filters);
  }

  // filter away manifestations and expressions from work based on holdings.
  // Use unfiltered work if the library has no holdings or if the updated data from the holdings API is not yet ready.
  // This means that there's a chance the displayed frontend data changes once the updated holdings are obtained, when
  // theholdings according to the data from the search backend deviates from the updated data. If this becomes an
  // issue we could show loaders until the updated data is available. For now we use a blurred version of the image
  // until the data is ready, assuming that a changing image is more confusing than text.

  public filterOnHoldings = (holdings?: Holdings): CordataWork => {
    if (!holdings) return this;

    //used to check if a manifestation has at least one item with valid holding state.
    //holding states can be seen here: https://bs-redia.atlassian.net/l/cp/GvJidqrC
    const manifestationHoldingStatusIsValid = ({ libraryCatalogueRecords = [], _origin }: Manifestation) => {
      if (
        supportedExternalContentServices.find(
          (externalContentService) => externalContentService.originName === _origin?.name
        )
      )
        return true;
      return libraryCatalogueRecords.some(({ localRecordId }) => {
        const holdingsDataForRecord = holdings?.[localRecordId];
        if (!holdingsDataForRecord) return false;
        const { reserveId } = holdingsDataForRecord;
        if (
          holdingsDataForRecord.locations.every((location) =>
            //checking if every holding state is invalid
            location.items.every(
              ({ loanableState, availableState }) =>
                (availableState === "available" && loanableState === "not_loanable" && !!reserveId) || //invalid state
                (availableState === "not_available" && loanableState === "not_loanable" && !reserveId) //lost item, deemed invalid state
            )
          )
        )
          return false;
        return true;
      });
    };

    const rawWork = this.getRawBmdbWork();

    const filteredRawWork = {
      ...rawWork,
      expressions: (rawWork.expressions ?? [])
        .map(({ manifestations = [], aggregateManifestations = [], ...rest }) => ({
          ...rest,
          manifestations: manifestations.filter(manifestationHoldingStatusIsValid),
          aggregateManifestations: aggregateManifestations.filter(manifestationHoldingStatusIsValid),
        }))
        .filter(
          ({ manifestations = [], aggregateManifestations = [] }) =>
            !!manifestations.length || !!aggregateManifestations.length
        ),
    };

    return new CordataWork(filteredRawWork);
  };

  get illustratedTag() {
    const documentTypes = this.getDocumentTypes();
    if (!documentTypes.some(({ format }) => ["bok", "ebok"].includes(format?.toLowerCase().replace("-", "")))) {
      return undefined;
    }

    const illustratedBookGenres = [
      "1166092", //bildebøker
      "1166093", //pekebøker
      "1103158", //Kunstnerbøker,
      "1453728", //Bilderomaner
    ];
    const comicGenres = [
      "1166096", //Tegneserier
      "1166043", //Grafiske romaner
    ];

    const illustratedWorkGenres = "1203040";

    if (
      this.work.genres?.some((genre) => {
        return illustratedBookGenres.includes(genre.identifiers.bibbiAuthorityId ?? "");
      })
    ) {
      return "Illustrert bok";
    }

    if (
      this.work.genres?.some((genre) => {
        return comicGenres.includes(genre.identifiers.bibbiAuthorityId ?? "");
      })
    ) {
      return "Tegneserie";
    }

    if (
      this.work.genres?.some((genre) => {
        return illustratedWorkGenres === genre.identifiers.bibbiAuthorityId;
      })
    ) {
      return "Illustrert verk";
    }

    return undefined;
  }
}
